// useProductForm.js
import {useEffect, useMemo, useState, useRef, useCallback} from "react";
import API from "../../../../network/dashboardAPI";
import UploadService from "../../../../services/FileUploadService";
import * as Yup from 'yup';
import useAlert from '../../../../services/useAlert';
import { fetchSupplierProductBySupplierID } from "../../SupplierProduct/services/supplierProductService";
import { KHMER_TEXT_BYTE_LIMIT, byteLength, isWithinKhmerByteLimit, toNullableRichText } from "../utils/khmerText";

/**
 * Optional Khmer rich-text field: blank is valid, the only limit is the
 * TEXT column's ~65,000 byte budget (~21,000 Khmer characters).
 */
const khmerTextSchema = (label) =>
	Yup.string()
		.nullable()
		.test(
			"khmer-byte-limit",
			({value}) =>
				`${label} is too long — ${byteLength(value || "").toLocaleString()} of ${KHMER_TEXT_BYTE_LIMIT.toLocaleString()} bytes. Khmer characters use 3 bytes each.`,
			isWithinKhmerByteLimit
		);

export default function useProducts(props, mode = "add", itemId, relatedProductsHook = null) {
	const alert = useAlert();

	const [initialValues, setInitialValues] = useState({
		itemName: "",
		itemCode: "",
		itemNameKhmer: "",
		itemAbout: "",
		itemAboutKm: "",
		howToUse: "",
		howToUseKm: "",
		ingredients: "",
		ingredientsKm: "",
		itemCat: null,
		itemSubCat: null,
		itemBrand: null,
		itemCountry: null,
		// itemSupplierId: null,
		vatRate: 0,
		itemPriceName: "",
		itemWeight: null,
		itemQuantity: 0,
		itemPrice: null,
		itemMrp: null,
		itemPriority: false,
		itemNewArrival: false,
		itemArrivalLimit: null,
		// itemPromoCost: false,
		// itemPromoCostPrice: null,
		// itemPromoCostStartDate: null,
		// itemPromoCostEndDate: null,
		itemPromo: false,
		itemPromoPrice: null,
		itemPromoStartDate: null,
		itemPromoEndDate: null,
		dpPrice: null,
		dpPoints: null,
		dpPromoStartDate: null,
		dpPromoEndDate: null,
		itemSameDay: false,
		itemAvailable: true,
		itemTrending: false,
		itemDietary: [],
		itemPriceId: 0,
		itemDisablePayOnDelivery: false,
		image: "",
		slider: []

	});

	const ProductSchemas = Yup.object().shape({
		itemName: Yup.string().required("Name is required"),
		itemCode: Yup.string().required("Code is required"),
		itemCat: Yup.string().nullable().required("Category is required"),
		itemSubCat: Yup.string().nullable().required("Sub Category is required"),
		itemCountry: Yup.string().nullable().required("Country is required"),
		itemPrice: Yup.number().required("Price is required"),
		// itemMrp: Yup.number().nullable(),
		// itemQuantity: Yup.number().nullable(),
		itemPriceName: Yup.string().required("Variant is required"),
		// Weight drives the shipping band at checkout. A product saved without it
		// contributes 0 g to the basket, which prices the whole order at the
		// lightest rate — so it is required on edit as well as create, making an
		// edit of a legacy product the moment its weight gets filled in.
		itemWeight: Yup.number()
			.transform((value, original) => (original === "" || original === null ? undefined : value))
			.typeError("Weight must be a number")
			.positive("Weight must be greater than 0")
			.required("Weight (g) is required"),
		// itemSupplierId: Yup.string().nullable().required("Supplier is required"),
		// Khmer fields are optional; the only rule is the TEXT column's byte budget,
		// which is not the same as the English varchar(5000) limit.
		itemAboutKm: khmerTextSchema("About Item (KH)"),
		howToUseKm: khmerTextSchema("How To Use (KH)"),
		ingredientsKm: khmerTextSchema("Ingredients (KH)"),
	});


	const [dietaryPreferences, setDietaryPreferences] = useState([]);


	const [imageFile, setImageFile] = useState(null);
	const [sliderFiles, setSliderFiles] = useState([]);
	const [removeSlider, setRemoveSlider] = useState([])
	const [loading, setLoading] = useState(false);

	const [imageUploading, setImageUploading] = useState(false);
	const [sliderUploading, setSliderUploading] = useState(false);

	const [products, setProducts] = useState([]);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [productTotal, setProductTotal] = useState(0);
	const [dialogImgOpen, setDialogImgOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});
	const [isView, setIsView] = useState(false);

	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [fetching, setFetching] = useState(false);

	// Supplier section state
	const [supplierSections, setSupplierSections] = useState([{
		element_id: 1,
		id: null,
		supplier: 0,
		supplier_set: null,
		quantity: null,
		productNumber: null,
		productNumberSet: null,
		productName: null,
		productNameSet: null,
		comment: null,
		unit: 'pcs', // Default to 'pcs'
		retailPrice: null,
		unitCost: null,
		promotionPrice: null,
		promotionPriceDate: null,
		isEnabled: false,
		order: 1
	}]);
	const [errors, setErrors] = useState({});
	const [deletedSupplierIds, setDeletedSupplierIds] = useState([]);
	const nextElementId = useRef(2);
	const [supplierProductsMap, setSupplierProductsMap] = useState({});
	const [supplierProductNumberMap, setSupplierProductNumberMap] = useState({});
	const [supplierProductNameMap, setSupplierProductNameMap] = useState({});
	const [isDisabled, setIsDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	// Promotion history popper states
	const promotionHistoryRefs = useRef({});
	const [openPromotionHistory, setOpenPromotionHistory] = useState({});
	const [promotionHistoryData, setPromotionHistoryData] = useState({});
	const [loadingPromotionHistory, setLoadingPromotionHistory] = useState({});

	// Price history popper states
	const priceHistoryRefs = useRef({});
	const [openPriceHistory, setOpenPriceHistory] = useState({});
	const [priceHistoryData, setPriceHistoryData] = useState({});
	const [loadingPriceHistory, setLoadingPriceHistory] = useState({});

	// Stock list popper states
	const stockListRef = useRef(null);
	const [openStockList, setOpenStockList] = useState(false);
	const [stockList, setStockList] = useState([]);
	const [acceptAndPackedOrder, setAcceptAndPackedOrder] = useState([]);
	const [availableForSale, setAvailableForSale] = useState(0);

	const handleChange = (field, value) => {
		setInitialValues((prev) => ({...prev, [field]: value}));
	};

	const onCategoryChange = (catId, setFieldValue) => {
		setInitialValues((prev) => ({...prev, itemCat: catId, itemSubCat: null}));
		setFieldValue("itemCat", catId);
		props.getSubCategory({cat_id: catId});
	};

	// Fetch all reference lists once
	useEffect(() => {
		async function fetchLists() {
			try {
				await Promise.all([
					props.getCategory({store_id: 1, get_type: 1}),
					props.getBrands({}),
					props.getSuppliers({}),
					props.getCountries({}),
					getDietaryPreferences(),
				]);
			} catch (err) {
				console.error("Failed to fetch reference lists:", err);
			}
		}

		fetchLists();
	}, []);

// Fetch product AFTER lists are ready
	useEffect(() => {
		async function fetchProduct() {
			if (mode !== "update" || !itemId) return;
			setLoading(true);

			try {
				const res = await API.getProductById({item_id: itemId});
				const response = await res.json();

				if (!response.data) return;

				const data = response.data;

				// Set price histories from response
				if (response.getPriceHistories) {
					setPriceHistoryData((prev) => ({ ...prev, 'main-price': response.getPriceHistories }));
				}
				if (response.getPromotionHistories) {
					setPromotionHistoryData((prev) => ({ ...prev, 'main-promotion': response.getPromotionHistories }));
				}

				// Process stock list
				const stocks = [];
				let sum_quantity = 0;
				if (response.getStocks) {
					response.getStocks.forEach(mapping => {
						const quantity = mapping.quantity;
						mapping.supplier_products_stocks.forEach(stock => {
							if (mapping.supplier_product.product_status == 1 || stock.warehouse_location.warehouse.is_main_warehouse == 1 || stock.warehouse_location.warehouse.is_ds_warehouse == 1) {
								const calculate_quantity = Math.floor(stock.available_quantity / quantity);
								stocks.push({
									quantity: quantity,
									available_quantity: stock.available_quantity,
									calculate_quantity: calculate_quantity,
									warehouse_name: stock.warehouse_location?.warehouse?.name || 'N/A',
									location_name: stock.warehouse_location?.name || 'N/A',
								});
								sum_quantity += calculate_quantity;
							}
						});
					});
				}
				setStockList(stocks);

				// Process accepted and packed orders
				const orders = [];
				let sum_of_order_quantity = 0;
				if (response.getAcceptAndPackedOrder) {
					response.getAcceptAndPackedOrder.forEach(mapping => {
						mapping.order_item_details.forEach(order => {
							if (order.item_id == itemId) {
								orders.push({
									order_id: order.order_id,
									quantity: order.quantity,
								});
								sum_of_order_quantity += order.quantity;
							}
						});
					});
				}
				setAcceptAndPackedOrder(orders);
				setAvailableForSale(sum_quantity - sum_of_order_quantity);

				// Load supplier sections if they exist
				if (response.data.map_products_supplier_products) {
					const suppliersRes = await API.getSuppliers({});
					const suppliersData = await suppliersRes.json();

					// Step 1: Build supplier sections from the response
					const sections = response.data.map_products_supplier_products.map((data, i) => {
						const element_id = i + 1;
						const stock_lvl = data.supplier_product?.supplier_products_stocks?.reduce((sum, stock) => sum + stock.available_quantity, 0) || 0;
						
						return {
							element_id,
							id: data.id,
							supplierProductID: data.supplier_product_id,
							quantity: data.quantity,
							supplier: data.supplier_product.supplier_id,
							supplier_set: suppliersData.data?.find(supplier => supplier.store_user_id === data.supplier_product.supplier_id),
							productNumber: data.supplier_product.supplier_product_number,
							productNumberSet: {
								value: data.supplier_product.supplier_product_number,
								label: data.supplier_product.supplier_product_number
							},
							productName: data.supplier_product.supplier_product_name,
							productNameSet: {
								value: data.supplier_product.supplier_product_name,
								label: data.supplier_product.supplier_product_name
							},
							promotionPrice: (data.supplier_product.is_promotion == 1 && data.promotion_histories?.[0]?.price) ? data.promotion_histories[0].price : null,
							promotionPriceDate: (data.supplier_product.is_promotion == 1 && data.promotion_histories?.[0]?.date_from && data.promotion_histories?.[0]?.date_to) ? (data.promotion_histories[0].date_from + ' - ' + data.promotion_histories[0].date_to) : null,
							comment: data.supplier_product.comment,
							unit: data.supplier_product.unit,
							retailPrice: data.supplier_product.retail_price,
							unitCost: data.supplier_product.unit_cost,
							stock: stock_lvl,
							order: data.order,
							isEnabled: data.supplier_product.product_status || false,
						};
					});

					const ordered = sections.map((section, index) => ({
						...section,
						order: section.order || (index + 1)
					}));
					setSupplierSections(ordered);
					nextElementId.current = ordered.length + 1;
					setDeletedSupplierIds([]);

					// For each section with a supplier, fetch and set supplier product options and values
					sections.forEach(async (section) => {
						if (section.supplier) {
							try {
								setIsLoading(true);
								const response = await fetchSupplierProductBySupplierID(section.supplier);
								setSupplierProductsMap((prev) => ({ ...prev, [section.element_id]: response.data || [] }));
								const productNumbers = (response.data || []).map(item => ({
									value: item.supplier_product_number,
									label: item.supplier_product_number,
								}));
								const productNames = (response.data || []).map(item => ({
									value: item.supplier_product_name,
									label: item.supplier_product_name,
								}));
								setSupplierProductNumberMap((prev) => ({ ...prev, [section.element_id]: productNumbers }));
								setSupplierProductNameMap((prev) => ({ ...prev, [section.element_id]: productNames }));
							} catch (error) {
								setSupplierProductsMap((prev) => ({ ...prev, [section.element_id]: [] }));
								setSupplierProductNumberMap((prev) => ({ ...prev, [section.element_id]: [] }));
								setSupplierProductNameMap((prev) => ({ ...prev, [section.element_id]: [] }));
							} finally {
								setIsLoading(false);
								setIsDisabled(false);
							}
						}
					});
				}

				// --- Parallel fetch for brand, supplier, category, subcategory ---
				const [brandRes, supplierRes, categoryRes, subCategoryRes] = await Promise.all([
					API.getBrandById(data.brand_id),
					API.getSuppliers({supplier_id: data.supplier_id}),
					API.getCategory({store_id: 1, get_type: 1, cat_id: data.cat_id}),
					API.getSubCategoryMap({cat_id: data.cat_id, sub_cat_id: data.sub_cat_id}),
				]);

				const brandJson = brandRes ? await brandRes.json() : null;
				const categoryJson = categoryRes ? await categoryRes.json() : null;
				const subCategoryJson = subCategoryRes ? await subCategoryRes.json() : null;
				const suppliersJson = supplierRes ? await supplierRes.json() : null;

				const brand = brandJson?.data || null;
				const suppliers = suppliersJson?.data || null;
				const supplier = suppliers?.[0] || null;
				const categories = categoryJson?.data || [];
				const category = categories?.[0] || null;
				const subCategories = subCategoryJson?.data || [];
				const sub_category = subCategories?.[0] || null;

				// --- Prepare React-Select compatible values ---
				const getOption = (list, id) => list?.find((item) => item.id === id) || null;

				setInitialValues({
					itemName: data.name || "",
					itemCode: data.product_code || "",
					itemNameKhmer: data.name_km || "",
					itemAbout: data.about_item || "",
					itemAboutKm: data.about_item_km || "",
					howToUse: data.how_to_use || "",
					howToUseKm: data.how_to_use_km || "",
					ingredients: data.ingredients || "",
					ingredientsKm: data.ingredients_km || "",
					itemCat: getOption(props.categories, data.cat_id) || category,
					itemSubCat: getOption(props.subCategories, data.sub_cat_id) || sub_category,
					itemCountry: getOption(props.countries, data.countries?.id) || data.countries || null,
					itemSupplierId: getOption(props.suppliers, data.supplier_id) || supplier,
					vatRate: data.vat_rate || 0,
					// itemSupplierId: getOption(props.suppliers, data.supplier_id) || supplier,
					itemPriceName: data.store_items_price_types?.[0]?.name || "",
					itemWeight: data.store_items_price_types?.[0]?.weight || null,
					itemPrice: data.store_items_price_types?.[0]?.price || null,
					itemMrp: data.store_items_price_types?.[0]?.mrp || null,
					itemQuantity: data.store_items_price_types?.[0]?.available_quantity || 0,
					itemPriceId: data.store_items_price_types?.[0]?.id || 0,
					itemPriority: data.priority || false,
					itemNewArrival: !!data.new_arrival,
					itemArrivalLimit: data.arrival_limit || null,
					// itemPromoCost:
					// 	data.promo_cost && new Date(data.promo_cost_end_date).getTime() >= Date.now(),
					// itemPromoCostPrice: data.promo_cost || null,
					// itemPromoCostStartDate: data.promo_cost_start_date || null,
					// itemPromoCostEndDate: data.promo_cost_end_date || null,
					itemPromo: data.promo || false,
					itemPromoPrice: data.promo_price || null,
					itemPromoStartDate: data.promo_start_date || null,
					itemPromoEndDate: data.promo_end_date || null,
					dpPrice: data.dp_price || null,
					dpPoints: data.dp_points || null,
					dpPromoStartDate: data.dp_promo_start_date || null,
					dpPromoEndDate: data.dp_promo_end_date || null,
					itemSameDay: data.same_day || false,
					itemAvailable: data.store_items_price_types?.[0]?.is_available ?? true,
					itemTrending: data.trending || false,
					itemDisablePayOnDelivery: data.disable_pay_on_delivery || false,
					itemDietary: (data.store_item_dietary_preferences || []).map((d) => d.dietary_preference),
					itemBrand: getOption(props.brands, data.brand_id) || brand,
					image: data.default_image_url || "",
					slider: data.store_item_sliders || [],
				});
				setSliderFiles(data.store_item_sliders || [])
			} catch (err) {
				console.error("Failed to fetch product:", err);
			} finally {
				setLoading(false);
			}
		}

		fetchProduct();
	}, [mode, itemId, props.categories, props.subCategories, props.brands, props.suppliers, props.countries, props.dietaryPreferences]);

	// Helper function to calculate duration
	const calculateDuration = (startDate, endDate) => {
		if (!startDate || !endDate) return '';

		const start = new Date(startDate);
		const end = new Date(endDate);
		const now = new Date();
		const diffMs = end - start;

		if (diffMs <= 0) return 'Invalid date range';

		// Check if promotion has expired
		if (now > end) {
			return 'Expired';
		}

		// Check if promotion hasn't started yet
		if (now < start) {
			const timeUntilStart = start - now;
			const daysUntilStart = Math.floor(timeUntilStart / (1000 * 60 * 60 * 24));
			const hoursUntilStart = Math.floor((timeUntilStart % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

			const parts = [];
			if (daysUntilStart > 0) parts.push(`${daysUntilStart} day${daysUntilStart > 1 ? 's' : ''}`);
			if (hoursUntilStart > 0) parts.push(`${hoursUntilStart} hour${hoursUntilStart > 1 ? 's' : ''}`);

			return `Starts in ${parts.join(', ')}`;
		}

		// Calculate remaining time (promotion is active)
		const remainingMs = end - now;
		const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));

		const parts = [];
		if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

		return parts.length > 0 ? parts.join(', ') + ' left' : 'Expires soon';
	}

	// Supplier section handlers
	const fetchPromotionHistory = async (supplierProductID, elementId) => {
		if (!supplierProductID) return;

		setLoadingPromotionHistory((prev) => ({ ...prev, [elementId]: true }));

		try {
			// Check if this is for main product or supplier product
			const isMainProduct = elementId === 'main-promotion';
			const requestBody = isMainProduct
				? { item_id: supplierProductID }
				: { supplier_product_id: supplierProductID };

			const response = await API.getPromotionHistory(requestBody);
			const data = await response.json();
			setPromotionHistoryData((prev) => ({ ...prev, [elementId]: data.data || [] }));
		} catch (error) {
			console.error('Error fetching promotion history:', error);
			setPromotionHistoryData((prev) => ({ ...prev, [elementId]: [] }));
		} finally {
			setLoadingPromotionHistory((prev) => ({ ...prev, [elementId]: false }));
		}
	};

	const handlePromotionHistoryEnter = useCallback((elementId, supplierProductID) => {
		setOpenPromotionHistory((prev) => ({ ...prev, [elementId]: true }));
		// Only fetch if it's a supplier product (not main product) and data doesn't exist
		const isMainProduct = elementId === 'main-promotion';
		if (!isMainProduct && !promotionHistoryData[elementId] && supplierProductID) {
			fetchPromotionHistory(supplierProductID, elementId);
		}
	}, [promotionHistoryData]);

	const handlePromotionHistoryLeave = useCallback((elementId) => {
		setOpenPromotionHistory((prev) => ({ ...prev, [elementId]: false }));
	}, []);

	const setPromotionHistoryRef = useCallback((elementId, ref) => {
		promotionHistoryRefs.current[elementId] = ref;
	}, []);

	const clearPromotionData = useCallback((elementId) => {
		setSupplierSections((prevSections) =>
			prevSections.map((section) =>
				section.element_id === elementId ? { ...section, promotionPrice: null, promotionPriceDate: null } : section
			)
		);
	}, []);

	// Price history handlers
	const fetchPriceHistory = async (elementId) => {
		setLoadingPriceHistory((prev) => ({ ...prev, [elementId]: true }));
		try {
			const requestBody =  { item_id: elementId }
			const response = await API.getPriceHistory(requestBody);
			const data = await response.json();
			setPriceHistoryData((prev) => ({ ...prev, [elementId]: data.data || [] }));
		} catch (error) {
			console.error('Error fetching price history:', error);
			setPriceHistoryData((prev) => ({ ...prev, [elementId]: [] }));
		} finally {
			setLoadingPriceHistory((prev) => ({ ...prev, [elementId]: false }));
		}
	};	const handlePriceHistoryEnter = useCallback((elementId) => {
		setOpenPriceHistory((prev) => ({ ...prev, [elementId]: true }));
		if (!priceHistoryData[elementId]) {
			fetchPriceHistory(elementId);
		}
	}, [priceHistoryData]);

	const handlePriceHistoryLeave = useCallback((elementId) => {
		setOpenPriceHistory((prev) => ({ ...prev, [elementId]: false }));
	}, []);

	const setPriceHistoryRef = useCallback((elementId, ref) => {
		priceHistoryRefs.current[elementId] = ref;
	}, []);

	const handleAddSupplierSection = () => {
		if (!validateSupplierSections()) {
			const errorMessages = Object.values(errors)
				.flatMap((sectionErrors) => Object.values(sectionErrors))
				.join("\n");

			alert.error(
				'Validation Error',
				`Fix the following errors before adding a new supplier section:\n\n${errorMessages}`
			);
			return;
		}
		const newId = nextElementId.current++;
		setSupplierSections((prevSections) => [
			...prevSections,
			{
				element_id: newId,
				id: null,
				supplier: 0,
				supplier_set: null,
				quantity: null,
				productNumber: null,
				productNumberSet: null,
				productName: null,
				productNameSet: null,
				comment: null,
				unit: 'pcs',
				retailPrice: null,
				unitCost: null,
				promotionPrice: null,
				promotionPriceDate: null,
				isEnabled: false,
				order: prevSections.length + 1
			},
		]);
	};

	const handleRemoveSupplierSection = (element_id) => {
		setSupplierSections((prevSections) => {
			const removed = prevSections.find(s => s.element_id === element_id);
			if (removed?.id) {
				setDeletedSupplierIds(prev => [...prev, removed.id]);
			}
			return prevSections
				.filter(section => section.element_id !== element_id)
				.map((section, index) => ({ ...section, order: index + 1 }));
		});

		setErrors((prevErrors) => {
			const newErrors = { ...prevErrors };
			delete newErrors[element_id];
			return newErrors;
		});
	};

	const updateSupplierData = async (element_id, field, value) => {
		if ((field === 'productNumber' || field === 'productName') && value) {
			if (supplierProductNumberMap[element_id] || supplierProductNameMap[element_id]) {
				const productsArray = supplierProductsMap[element_id] || [];
				const product = productsArray.find((p) => (p.supplier_product_number === value.value || p.supplier_product_name === value.value));
				if (product) {
					let promoPrice = (product.promotion_histories[0] && product.promotion_histories[0].price) ? product.promotion_histories[0].price : null;
					let promoPriceDate = (product.promotion_histories[0] && product.promotion_histories[0].date_from && product.promotion_histories[0].date_to) ? product.promotion_histories[0].date_from + ' - ' + product.promotion_histories[0].date_to : null;
					setSupplierSections((prevSections) =>
						prevSections.map((section) =>
							section.element_id === element_id
								? {
										...section,
										supplierProductID: product.id,
										productNumber: product.supplier_product_number,
										productNumberSet: { value: product.supplier_product_number, label: product.supplier_product_number },
										productName: product.supplier_product_name,
										productNameSet: { value: product.supplier_product_name, label: product.supplier_product_name },
										retailPrice: product.retail_price,
										unitCost: product.unit_cost,
										unit: product.unit,
										comment: product.comment,
										promotionPrice: promoPrice,
										promotionPriceDate: promoPriceDate,
									}
								: section
						)
					);
				} else {
					if (field === 'productNumber') {
						setSupplierSections((prevSections) =>
							prevSections.map((section) =>
								section.element_id === element_id
									? { ...section,
											productNumber: value ? value.value : null,
											productNumberSet: { value: value ? value.value : null, label: value ? value.value : null },
									}
									: section
							)
						);
					} else if (field === 'productName') {
						setSupplierSections((prevSections) =>
							prevSections.map((section) =>
								section.element_id === element_id
									? { ...section,
											productName: value ? value.value : null,
											productNameSet: { value: value ? value.value : null, label: value ? value.value : null },
									}
									: section
							)
						);
					}
				}
			} else {
				setSupplierSections((prevSections) =>
					prevSections.map((section) =>{
					if (section.element_id !== element_id) return section;

					if (field === 'supplier') {
						return {
							...section,
							supplier: value ? value.store_user_id : null,
							supplier_set: value || null,
							productNumber: null,
							productNumberSet: null,
							productName: null,
							productNameSet: null,
						};
					}

					if (field === 'productNumber') {
						return {
							...section,
							productNumber: value ? value.value : null,
							productNumberSet: { value: value ? value.value : null, label: value ? value.value : null },
						};
					}

					if (field === 'productName') {
						return {
							...section,
							productName: value ? value.value : null,
							productNameSet: { value: value ? value.value : null, label: value ? value.value : null },
						};
					}

					return {
						...section,
						[field]: value ?? null,
					};
				}))
			}
		} else {
			setSupplierSections((prevSections) =>
				prevSections.map((section) => {
					if (section.element_id !== element_id) return section;

					if (field === 'supplier') {
						return {
							...section,
							supplier: value ? value.store_user_id : null,
							supplier_set: value || null,
							productNumber: null,
							productNumberSet: null,
							productName: null,
							productNameSet: null,
						};
					}

					if (field === 'productNumber') {
						return {
							...section,
							productNumber: value ? value.value : null,
							productNumberSet: { value: value ? value.value : null, label: value ? value.value : null },
						};
					}

					if (field === 'productName') {
						return {
							...section,
							productName: value ? value.value : null,
							productNameSet: { value: value ? value.value : null, label: value ? value.value : null },
						};
					}

					return {
						...section,
						[field]: value ?? null,
					};
				})
			);
		}

		if (field === 'supplier' && value) {
			try {
				setIsLoading(true);
				const response = await fetchSupplierProductBySupplierID(value.store_user_id);
				setSupplierProductsMap((prev) => ({ ...prev, [element_id]: response.data || [] }));
				const productNumbers = (response.data || []).map(item => ({
					value: item.supplier_product_number,
					label: item.supplier_product_number,
				}));
				const productNames = (response.data || []).map(item => ({
					value: item.supplier_product_name,
					label: item.supplier_product_name,
				}));
				setSupplierProductNumberMap((prev) => ({ ...prev, [element_id]: productNumbers }));
				setSupplierProductNameMap((prev) => ({ ...prev, [element_id]: productNames }));
			} catch (error) {
				console.error('Error fetching supplier products:', error);
				setSupplierProductsMap((prev) => ({ ...prev, [element_id]: [] }));
				setSupplierProductNumberMap((prev) => ({ ...prev, [element_id]: [] }));
				setSupplierProductNameMap((prev) => ({ ...prev, [element_id]: [] }));
			} finally {
				setIsLoading(false);
				setIsDisabled(false);
			}
		}
	};

	const toggleSupplierEnabled = (element_id) => {
		setSupplierSections((prevSections) =>
			prevSections.map(section =>
					section.element_id === element_id ? { ...section, isEnabled: !section.isEnabled } : section
			)
		);
	};

	const collectSupplierData = () => {
		return supplierSections.map(section => ({
			id: section.id,
			supplierId: section.supplier,
			quantity: section.quantity,
			productNumber: section.productNumber,
			productName: section.productName,
			comment: section.comment,
			unit: section.unit || 'pcs', // Default to 'pcs' if unit is empty
			retailPrice: section.retailPrice,
			unitCost: section.unitCost,
			promotionPrice: section.promotionPrice,
			promotionPriceDate: section.promotionPriceDate,
			order: section.order,
			isEnabled: section.isEnabled,
		}));
	};

	const handleOnDragEnd = (result) => {
		if (!result.destination) {
			return;
		}

		const items = Array.from(supplierSections);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);

		// Update order values based on new positions (1-based indexing)
		const updatedItems = items.map((item, index) => ({
			...item,
			order: index + 1
		}));

		setSupplierSections(updatedItems);
	};

	const validateSupplierSections = () => {
		let newErrors = {};
		let isValid = true;

		supplierSections.forEach((section) => {
			let sectionErrors = {};

			if (!section.supplier || section.supplier === "0") {
				sectionErrors.supplier = "Supplier is required.";
				isValid = false;
			}
			if (!section.quantity) {
				sectionErrors.quantity = 'Quantity is required';
				isValid = false;
			} else if (parseFloat(section.quantity) <= 0) {
				sectionErrors.quantity = "Quantity must be greater than 0.";
				isValid = false;
			}

			if (!section.productNumber) {
				sectionErrors.productNumber = "Product Number is required.";
				isValid = false;
			}
			if (!section.productName) {
				sectionErrors.productName = "Product Name is required.";
				isValid = false;
			}

			// No validation for unit - will default to 'pcs' if empty

			if (!section.retailPrice || isNaN(section.retailPrice) || section.retailPrice <= 0) {
				sectionErrors.retailPrice = "Valid Retail Price is required.";
				isValid = false;
			}

			if (!section.unitCost || isNaN(section.unitCost) || section.unitCost <= 0) {
				sectionErrors.unitCost = "Valid Unit Cost is required.";
				isValid = false;
			}

			if (Object.keys(sectionErrors).length > 0) {
				newErrors[section.element_id] = sectionErrors;
			}
		});
		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (values) => {
		// Validate supplier sections before submitting
		if (!validateSupplierSections()) {
			const errorMessages = Object.values(errors)
				.flatMap((sectionErrors) => Object.values(sectionErrors))
				.join("\n");

			alert.error(
				'Validation Error',
				`Fix the following errors before submitting:\n\n${errorMessages}`
			);
			return;
		}

		setLoading(true);

		// Image is now optional - prepare format based on whether it exists
		let image_url = ""
		if (mode === "add") {
			// For add mode, send image as array format if image exists, empty array otherwise
			image_url = values.image ? [{image_url: values.image}] : []
		} else {
			// For update mode, send image if exists, empty array otherwise
			image_url = values.image ? [{image_url: values.image}] : []
		}
		
		console.log('Image data being sent:', image_url);
		
		// Get selected related products from the hook if available
		const relatedProductIds = relatedProductsHook?.selectedProducts?.map((item) => item.item_id) || [];

		// Collect supplier data
		const supplierData = collectSupplierData();

		let postData = {
			image: image_url,
			deleted_supplier_ids: deletedSupplierIds,
			name: values.itemName,
			cat_id: values.itemCat?.cat_id,
			sub_cat_id: values.itemSubCat?.sub_cat_id,
			brand_id: values.itemBrand?.brand_id,
			country_id: values.itemCountry?.id,
			about_item: values.itemAbout,
			about_item_km: toNullableRichText(values.itemAboutKm),
			how_to_use: values.howToUse,
			how_to_use_km: toNullableRichText(values.howToUseKm),
			ingredients: values.ingredients,
			ingredients_km: toNullableRichText(values.ingredientsKm),
			store_id: 1,
			prices: [
				{
					price_id: values.itemPriceId,
					store_id: 1,
					name: values.itemPriceName,
					price: values.itemPrice,
					mrp: values.itemMrp,
					is_available: values.itemAvailable,
					available_quantity: parseInt(values.itemQuantity, 10) || 0,
					weight: values.itemWeight ? parseFloat(values.itemWeight) : null,
					weight_unit: values.itemWeight ? 'g' : null,
				},
			],
			max_quantity_to_be_added: 1000000,
			product_code: values.itemCode,
			same_day: values.itemSameDay,
			supplier_id: values.itemSupplierId?.store_user_id,
			vat_rate: parseFloat(values.vatRate) || 0,
			// supplier_id: values.itemSupplierId?.store_user_id,
			suppliers: supplierData,
			promo: parseFloat(values.itemPromoPrice) > 0 ? true : false,
			promo_price: values.itemPromoPrice,
			promo_start_date: values.itemPromoStartDate,
			promo_end_date: values.itemPromoEndDate,
			dp_price: values.dpPrice ? parseFloat(values.dpPrice) : null,
			dp_points: values.dpPoints ? parseInt(values.dpPoints, 10) : null,
			dp_promo_start_date: values.dpPromoStartDate || null,
			dp_promo_end_date: values.dpPromoEndDate || null,
			// promo_cost: parseInt(values.itemPromoCostPrice) > 0 ? true : false,
			// promo_cost_price: values.itemPromoCostPrice,
			// promo_cost_start_date: values.itemPromoCostStartDate,
			// promo_cost_end_date: values.itemPromoCostEndDate,
			new_arrival: values.itemNewArrival,
			arrival_limit: values.itemArrivalLimit,
			priority: values.itemPriority,
			trending: values.itemTrending,
			name_km: values.itemNameKhmer,
			related_ids: relatedProductIds,
			itemDietary: values.itemDietary || [],
			disable_pay_on_delivery: values.itemDisablePayOnDelivery ? 1 : 0,
		};

		try {
			// Show loading alert using the dedicated loading method
			alert.loading(
				`${mode === "add" ? "Adding" : "Updating"} Product...`,
				'Please wait while we save your product details'
			);

			if (mode === "add") {
				await props.addProduct(postData, sliderFiles);
			} else {
				postData.item_id = itemId;
				await props.updateProduct(postData, sliderFiles, removeSlider);
			}

			// Close loading alert
			alert.close();

			// Show success message
			alert.success(
				`${mode === "add" ? "Product Added!" : "Product Updated!"}`,
				`${values.itemName} has been ${mode === "add" ? "added to" : "updated in"} your store successfully.`
			);

			// Show toast notification
			alert.toast(`Product ${mode === "add" ? "added" : "updated"} successfully`);
			setDeletedSupplierIds([]);

		} catch (error) {
			console.error('Operation failed:', error);

			// Close loading alert on error
			alert.close();

			alert.error(
				`${mode === "add" ? "Add" : "Update"} Failed`,
				error.message || `Could not ${mode === "add" ? "add" : "update"} the product. Please try again.`
			);
		} finally {
			setLoading(false);
		}
	};

	// Optional: Add confirmation for expensive operations
	const handleSubmitWithConfirmation = async (values) => {
		if (mode === "add") {
			// For new products, proceed directly
			await handleSubmit(values);
		} else {
			// For updates, show confirmation
			const result = await alert.confirm(
				'Update Product?',
				'Are you sure you want to update this product?',
				'Yes, Update',
				'Cancel'
			);

			if (result.isConfirmed) {
				await handleSubmit(values);
			}
		}
	};

	// derive country options for react-select
	const countriesOptions = useMemo(() => {
		if (!props.countries) return [];
		return props.countries.map((c) => ({...c, label: c.name, value: c.code || c.name}));
	}, [props.countries]);

	// Upload product image
	const uploadProductImage = async (e, setFieldValue) => {
		const file = e.target.files[0];
		if (!file) return;
		
		console.log('Starting image upload for file:', file.name);
		setImageUploading(true);
		
		const formData = new FormData();
		formData.append("image", file);

		try {
			const response = await UploadService.uploadImageFromDashboard(formData);
			console.log('Upload response:', response.data);
			
			if (response.data && response.data.success) {
				const imageUrl = response.data.data[0].url;
				console.log('Image uploaded successfully. URL:', imageUrl);
				
				// ONLY update Formik's field value to avoid form reinitialization
				setFieldValue("image", imageUrl);
				
				// Also update initialValues for the preview, but do it without triggering reinit
				if (mode === "update") {
					setInitialValues((prev) => ({
						...prev,
						image: imageUrl,
					}));
				}
				
				console.log('Image URL set in form');
				setImageUploading(false);

				// Show success message
				alert.toast("Image uploaded successfully");
				
				// Auto-save for update mode
				if (mode === "update" && itemId) {
					await updateProductImage(imageUrl);
				}
			} else {
				throw new Error("Upload response was not successful");
			}
		} catch (error) {
			console.error("Error uploading image:", error);
			setImageUploading(false);
			alert.error("Upload Failed", "Failed to upload image. Please try again.");
		}
	};

	// Helper function to update product image
	const updateProductImage = async (imageUrl) => {
		try {
			alert.loading("Updating Product Image...", "Please wait");
			
			const postData = {
				item_id: itemId,
				store_id: 1,
				image: [{image_url: imageUrl}],
			};
			
			await API.updateProductImage(postData);
			
			alert.close();
			alert.toast("Product image updated successfully");
		} catch (error) {
			console.error("Error updating product image:", error);
			alert.close();
			alert.error("Update Failed", "Failed to update product image. Please try again.");
		}
	};

	// Upload product sliders
	const uploadProductSliders = async (e, setFieldValue, currentValues = null) => {
		const files = e.target.files;
		setSliderUploading(true);

		if (files && files.length > 0) {
			try {
				// For update mode, upload immediately to backend
				if (mode === "update" && itemId) {
					// Prepare FormData for upload
					const formData = new FormData();
					for (let i = 0; i < files.length; i++) {
						formData.append("image", files[i]);
					}
					formData.append('store_id', "1");
					formData.append("item_id", itemId);

					alert.loading("Uploading Slider Images...", "Please wait");

					const response = await UploadService.uploadSliderFromDashboard(formData);
					
					if (response.data.success) {
						// Refetch product to get updated slider list with IDs
						const res = await API.getProductById({item_id: itemId});
						const productResponse = await res.json();
						if (productResponse.data) {
							const sliders = productResponse.data.store_item_sliders || [];
							setFieldValue("slider", sliders);
							setInitialValues((prev) => ({
								...prev,
								slider: sliders
							}));
						}
						
						alert.close();
						alert.toast("Slider images uploaded successfully");
					} else {
						throw new Error("Upload failed");
					}
				} else {
					// For add mode, merge new files with existing ones
					// Get existing sliders from current Formik values
					const existingSliders = currentValues?.slider || [];
					const existingFiles = Array.from(sliderFiles || []);
					
					// Create new file URLs for the newly selected files
					const newFileUrls = Array.from(files).map(file => ({image_url: URL.createObjectURL(file)}));
					
					// Merge existing sliders with new ones
					const mergedSliders = [...existingSliders, ...newFileUrls];
					const mergedFiles = [...existingFiles, ...Array.from(files)];
					
					// Update states - only setFieldValue for add mode to avoid duplication
					setSliderFiles(mergedFiles);
					setFieldValue("slider", mergedSliders);
					
					alert.toast(`${files.length} image(s) added successfully`);
				}

				setSliderUploading(false);
			} catch (error) {
				console.error("Error uploading sliders:", error);
				setSliderUploading(false);
				alert.close();
				alert.error("Upload Failed", "Failed to upload slider images. Please try again.");
			}
		} else {
			setSliderUploading(false);
		}
	};

	// Form validation
	const [formError, setFormError] = useState({
		itemName: false,
		itemCode: false,
		itemCat: false,
		itemCountry: false,
		itemSubCat: false,
		itemPrice: false,
		// itemMrp: false,
		// itemQuantity: false,
		itemPriceName: false,
		// itemSupplierId: false,
		duplicateSKU: false,
	});

	const isAlreadySKU = async (value) => {
		try {
			const response = await API.getIsExistsProductCode({sku: value});
			const res = await response.json();
			if (!res.success) throw new Error("Failed to check SKU");
			return res.data;
		} catch (error) {
			console.error("Error checking SKUs:", error);
			return 0
		}
	};

	const getDietaryPreferences = async () => {
		try {
			const response = await API.getDietaryPreferencesList();
			const data = await response.json();
			setDietaryPreferences(data.data);
			return data.data;
		} catch (error) {
			console.error("Error Dietary:", error);
			throw error;
		}
	};

	const checkFormValidate = async () => {
		const {
			itemName,
			itemCat,
			itemSubCat,
			itemPrice,
			itemCode,
			itemCountry,
			itemPriceName,
			// itemMrp,
			// itemQuantity,
			// itemSupplierId,
		} = initialValues;

		let tmpFormError = {
			itemName: !itemName,
			itemCat: !itemCat || itemCat === 0,
			itemSubCat: !itemSubCat || itemSubCat === 0,
			itemPrice: !itemPrice,
			itemCode: !itemCode,
			itemCountry: !itemCountry,
			itemPriceName: !itemPriceName,
			// itemMrp: !itemMrp,
			// itemQuantity: !itemQuantity || itemQuantity < 0,
			// itemSupplierId: !itemSupplierId || itemSupplierId === 0,
			duplicateSKU: false,
		};

		if (itemCode) {
			try {
				const isDuplicate = await isAlreadySKU();
				if (isDuplicate === 1) {
					tmpFormError.duplicateSKU = true;
					tmpFormError.itemCode = true;
				}
			} catch (error) {
				tmpFormError.duplicateSKU = true;
				tmpFormError.itemCode = true;
			}
		}

		setFormError(tmpFormError);
		return Object.initialValues(tmpFormError).every((el) => el === false);
	};

	const handleRemoveSliders = async (slider, currentValues, setFieldValue) => {
		// Use currentValues if provided (from Formik), otherwise fall back to initialValues
		const currentSliders = currentValues?.slider || initialValues.slider || [];
		let newSliders = currentSliders.filter(s => s.image_url !== slider.image_url);
		
		if (mode === "add") {
			// For add mode, only update Formik field value to avoid duplication
			if (setFieldValue) {
				setFieldValue("slider", newSliders);
			}
			
			// Also update sliderFiles by removing the corresponding file
			if (sliderFiles && sliderFiles.length > 0) {
				const sliderIndex = currentSliders.findIndex(s => s.image_url === slider.image_url);
				if (sliderIndex !== -1) {
					const newFiles = Array.from(sliderFiles).filter((_, index) => index !== sliderIndex);
					setSliderFiles(newFiles);
				}
			}
			
			alert.toast("Image removed successfully");
		} else {
			// For update mode, call backend API to delete
			if (slider.id) {
				try {
					alert.loading("Removing Image...", "Please wait");
					
					const response = await UploadService.removeSliderFromDashboard({
						id: slider.id
					});
					
					alert.close();
					
					if (response.data && response.data.success) {
						// Update both states on success for update mode
						setInitialValues((prev) => ({
							...prev,
							slider: newSliders
						}));
						
						if (setFieldValue) {
							setFieldValue("slider", newSliders);
						}
						
						alert.toast("Image removed successfully");
					} else {
						// Show error if API call failed
						alert.error("Remove Failed", "Failed to remove image. Please try again.");
					}
				} catch (error) {
					console.error("Error removing slider:", error);
					alert.close();
					alert.error("Remove Failed", "Failed to remove image. Please try again.");
				}
			}
		}
	};	
	return {
		initialValues,
		setInitialValues,
		handleChange,
		handleSubmit,
		uploadProductImage,
		uploadProductSliders,
		loading,
		search,
		setSearch,
		page,
		setPage,
		rowsPerPage,
		setRowsPerPage,
		fetching,
		setFetching,
		products,
		setProducts,
		selectedProducts,
		setSelectedProducts,
		productTotal,
		setProductTotal,
		dialogImgOpen,
		setDialogImgOpen,
		selectedItem,
		setSelectedItem,
		isView,
		setIsView,
		formError,
		checkFormValidate,
		ProductSchemas,
		countriesOptions,
		imageFile,
		setImageFile,
		sliderFiles,
		setSliderFiles,
		imageUploading,
		sliderUploading,
		onCategoryChange,
		getDietaryPreferences,
		dietaryPreferences,
		isAlreadySKU,
		handleRemoveSliders,
		calculateDuration,
		handleSubmitWithConfirmation,
		// Supplier section exports
		supplierSections,
		setSupplierSections,
		errors,
		setErrors,
		supplierProductsMap,
		supplierProductNumberMap,
		supplierProductNameMap,
		isDisabled,
		isLoading,
		promotionHistoryRefs,
		openPromotionHistory,
		promotionHistoryData,
		loadingPromotionHistory,
		handleAddSupplierSection,
		handleRemoveSupplierSection,
		updateSupplierData,
		toggleSupplierEnabled,
		collectSupplierData,
		handleOnDragEnd,
		validateSupplierSections,
		fetchPromotionHistory,
		handlePromotionHistoryEnter,
		handlePromotionHistoryLeave,
		setPromotionHistoryRef,
		clearPromotionData,
		// Price history exports
		priceHistoryRefs,
		openPriceHistory,
		priceHistoryData,
		loadingPriceHistory,
		handlePriceHistoryEnter,
		handlePriceHistoryLeave,
		setPriceHistoryRef,
		// Stock list exports
		stockListRef,
		openStockList,
		setOpenStockList,
		stockList,
		acceptAndPackedOrder,
		availableForSale,
	};
}
